/**
 * ============================================================================
 * Court Validation - Input validation with user-friendly error messages
 * ============================================================================
 *
 * Comprehensive validation that was missing from old implementation
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from './currency';
import type { CourtFormData } from '../types/courts';

export interface ValidationError {
  field: string;
  message: string;
}

// Industry standard: Court names max 50 characters (fits in mobile UI, database varchar)
const COURT_NAME_MAX_LENGTH = 50;
const COURT_NAME_MIN_LENGTH = 3;

/**
 * Validate court name
 */
export function validateCourtName(name: string): string | null {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return 'Court name is required';
  }

  if (trimmedName.length < COURT_NAME_MIN_LENGTH) {
    return `Court name must be at least ${COURT_NAME_MIN_LENGTH} characters`;
  }

  if (trimmedName.length > COURT_NAME_MAX_LENGTH) {
    return `Court name cannot exceed ${COURT_NAME_MAX_LENGTH} characters`;
  }

  // Check for invalid characters (only allow alphanumeric, spaces, hyphens, and common symbols)
  const validNamePattern = /^[a-zA-Z0-9\s\-#().]+$/;
  if (!validNamePattern.test(trimmedName)) {
    return 'Court name contains invalid characters';
  }

  return null; // Valid
}

/**
 * Validate court form data
 * Returns array of validation errors (empty if valid)
 */
export function validateCourtForm(formData: CourtFormData): ValidationError[] {
  const errors: ValidationError[] = [];

  // Court name validation
  const nameError = validateCourtName(formData.courtName);
  if (nameError) {
    errors.push({
      field: 'courtName',
      message: nameError,
    });
  }

  // Sport selection is required
  if (formData.selectedSport === '' || formData.selectedSport === undefined || formData.selectedSport === null) {
    errors.push({
      field: 'selectedSport',
      message: 'Please select a sport for this court',
    });
  }

  // Validate custom hourly rate if provided
  if (formData.customHourlyRate !== '' && formData.customHourlyRate !== undefined) {
    const rate = typeof formData.customHourlyRate === 'number'
      ? formData.customHourlyRate
      : Number(formData.customHourlyRate);

    if (isNaN(rate)) {
      errors.push({
        field: 'customHourlyRate',
        message: 'Hourly rate must be a valid number',
      });
    } else if (rate < 0) {
      errors.push({
        field: 'customHourlyRate',
        message: 'Hourly rate cannot be negative',
      });
    } else if (rate > 1000000) {
      errors.push({
        field: 'customHourlyRate',
        message: `Hourly rate too high (maximum: ${CURRENCY_SYMBOL}1,000,000)`,
      });
    }
  }

  // Validate discount percentages (empty string is treated as 0)
  const discounts = [
    { value: formData.discount1Month, field: 'discount1Month', label: '1 month discount' },
    { value: formData.discount3Months, field: 'discount3Months', label: '3 months discount' },
    { value: formData.discount6Months, field: 'discount6Months', label: '6 months discount' },
    { value: formData.discount12Months, field: 'discount12Months', label: '12 months discount' },
  ];

  discounts.forEach(({ value, field, label }) => {
    // Empty string is valid (treated as 0)
    if (value === '') return;

    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) {
      errors.push({
        field,
        message: `${label} must be a valid number`,
      });
    } else if (numValue < 0) {
      errors.push({
        field,
        message: `${label} cannot be negative`,
      });
    } else if (numValue > 100) {
      errors.push({
        field,
        message: `${label} cannot exceed 100%`,
      });
    }
  });

  return errors;
}

/**
 * Check if form has any errors
 */
export function isFormValid(formData: CourtFormData): boolean {
  return validateCourtForm(formData).length === 0;
}

/**
 * Get error message for a specific field
 */
export function getFieldError(
  errors: ValidationError[],
  fieldName: string
): string | undefined {
  const error = errors.find((e) => e.field === fieldName);
  return error?.message;
}

/**
 * Validate discount percentage (0-100)
 */
export function validateDiscount(value: number): boolean {
  return value >= 0 && value <= 100;
}

/**
 * Validate hourly rate (0-1,000,000)
 */
export function validateRate(value: number): boolean {
  return value >= 0 && value <= 1000000;
}
